// frontend/src/utils/auditorApi.js
// Shared API helpers for the auditor portal.
// Centralises paginated-fetch logic and error handling so every auditor
// sub-component behaves consistently.

/**
 * Fetch ALL pages from a DRF-paginated endpoint.
 *
 * Handles three response shapes:
 *   1. Plain array (non-paginated)
 *   2. DRF PageNumberPagination { count, next, results }
 *   3. Empty / error — returns [] and logs the failure.
 *
 * Errors are caught internally so a single failing endpoint never
 * crashes `Promise.all` and never leaves the rest of the data empty.
 *
 * @param {import('axios').AxiosInstance} authAxios
 * @param {string} url  Relative API path, e.g. 'invoices/'
 * @returns {Promise<Array>}
 */
export async function fetchAllPages(authAxios, url) {
  let allResults = [];
  let nextUrl = url;
  let guard = 0;

  try {
    while (nextUrl && guard < 100) {
      guard++;
      const resp = await authAxios.get(nextUrl);
      const data = resp?.data;

      // 1. Plain array (non-paginated endpoint)
      if (Array.isArray(data)) {
        allResults = data;
        break;
      }

      // 2. Paginated response
      if (data?.results) {
        allResults = allResults.concat(data.results);
      }
      nextUrl = data?.next || null;

      // Normalise absolute next-page URLs to relative paths so axios
      // uses the configured baseURL instead of hard-coding the host.
      if (nextUrl && nextUrl.startsWith('http')) {
        try {
          const u = new URL(nextUrl);
          nextUrl = u.pathname + u.search;
        } catch {
          break;
        }
      }
    }
  } catch (err) {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail || err.message;
    console.error(`[Auditor] fetchAllPages("${url}") failed — ${status || 'network error'}: ${detail}`);
    // Return whatever we collected (may be [])
  }

  if (allResults.length === 0) {
    console.warn(`[Auditor] fetchAllPages("${url}") returned 0 results`);
  } else {
    console.log(`[Auditor] fetchAllPages("${url}") → ${allResults.length} records`);
  }

  return allResults;
}

/**
 * Convenience wrapper for report-style (non-paginated) endpoints.
 * Returns the response data or null on error.
 *
 * @param {import('axios').AxiosInstance} authAxios
 * @param {string} url
 * @param {object} [params]
 * @returns {Promise<object|null>}
 */
export async function fetchReport(authAxios, url, params = {}) {
  try {
    const resp = await authAxios.get(url, { params });
    return resp.data;
  } catch (err) {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail || err.message;
    console.error(`[Auditor] fetchReport("${url}") failed — ${status || 'network error'}: ${detail}`);
    return null;
  }
}
